/** "6109100012" → "6109.10.00.12"; "610910" → "6109.10"; "8471" → "8471". */
export function formatHsCode(code: string | null | undefined): string {
  if (!code) return '';
  const digits = code.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  const parts = [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
    digits.slice(10, 14),
  ].filter(Boolean);
  return parts.join('.');
}

export function formatMoney(
  amount: number | null | undefined,
  currency = 'USD',
  locale = 'en-US',
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Format integer minor units (cents) as currency. */
export function formatCents(cents: number, currency = 'usd', locale = 'en-US'): string {
  return formatMoney(cents / 100, currency.toUpperCase(), locale);
}

export function formatCredits(credits: number): string {
  return `${new Intl.NumberFormat('en-US').format(credits)} ${credits === 1 ? 'credit' : 'credits'}`;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Truncate a string for storage/logging without breaking surrogate pairs. */
export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${Array.from(value)
    .slice(0, max - 1)
    .join('')}…`;
}

/** JSON.stringify with a hard size cap (for usage_event input/output columns). */
export function safeJson(value: unknown, maxLength = 16_000): string | null {
  if (value === undefined) return null;
  try {
    const json = JSON.stringify(value);
    if (json.length <= maxLength) return json;
    return JSON.stringify({ truncated: true, preview: json.slice(0, maxLength - 64) });
  } catch {
    return null;
  }
}

/**
 * A value to store in a jsonb column, capped at `maxLength` characters of JSON: oversized values become
 * `{ truncated: true, preview }` (still valid JSON). Null for undefined or unserialisable values.
 */
/**
 * The value with every long `data:` URL (a photo sent inline) replaced by a note of its type and size: logs keep the
 * request, not the file. Only plain objects and arrays are walked.
 */
export function withoutInlineData(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    if (value.length <= 256 || !value.startsWith('data:')) return value;
    const type = /^data:([\w.+-]+\/[\w.+-]+)/.exec(value)?.[1] ?? 'unknown type';
    return `[inline ${type}, ${Math.max(1, Math.round((value.length * 0.75) / 1024))} KB, not kept]`;
  }
  if (depth > 8 || !value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => withoutInlineData(item, depth + 1));
  const proto = Object.getPrototypeOf(value) as unknown;
  if (proto !== Object.prototype && proto !== null) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, withoutInlineData(item, depth + 1)]),
  );
}

export function jsonbSafe(value: unknown, maxLength = 16_000): unknown {
  if (value === undefined) return null;
  try {
    const json = JSON.stringify(value);
    if (json === undefined) return null;
    if (json.length <= maxLength) return JSON.parse(json) as unknown;
    return { truncated: true, preview: json.slice(0, maxLength - 64) };
  } catch {
    return null;
  }
}

/**
 * One CSV cell. Quoted when it holds a comma, quote or line break; text that would start a spreadsheet formula (`=`,
 * `+`, `-`, `@`, a tab or carriage return) gets a leading `'`, so opening the file never runs one. Numbers stay as they
 * are.
 */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = typeof value === 'string' ? value : String(value);
  if (typeof value === 'string' && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** CSV text from rows of cells (see `csvCell`). */
export const csvText = (rows: ReadonlyArray<readonly unknown[]>): string =>
  rows.map((row) => row.map(csvCell).join(',')).join('\n');

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Parse an amount a person typed, in either convention: "1,250.50", "1.250,50", "49,99", "1 250", "€49.99".
 * When both "." and "," appear, the last one is the decimal separator. A lone "," is a decimal separator unless it
 * is followed by exactly three digits ("1,250" is 1250, "12,5" is 12.5). Spaces and apostrophes are group
 * separators, and a currency symbol or code around the number is ignored. Returns undefined if it isn't a number.
 */
export function parseAmount(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const text = value
    .trim()
    .replace(/[\s  '’]/g, '')
    .replace(/^[^\d.,+-]+/, '')
    .replace(/[^\d.,]+$/, '');
  if (!/^[+-]?[\d.,]*\d[\d.,]*$/.test(text)) return undefined;
  const lastDot = text.lastIndexOf('.');
  const lastComma = text.lastIndexOf(',');
  let normalized: string;
  if (lastDot >= 0 && lastComma >= 0) {
    const decimal = lastDot > lastComma ? '.' : ',';
    normalized = text
      .split(decimal === '.' ? ',' : '.')
      .join('')
      .replace(decimal, '.');
  } else if (lastComma >= 0) {
    const grouping = text.indexOf(',') !== lastComma || /^[+-]?\d{1,3},\d{3}$/.test(text);
    normalized = grouping ? text.replace(/,/g, '') : text.replace(',', '.');
  } else {
    normalized = text.indexOf('.') !== lastDot ? text.replace(/\./g, '') : text;
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? number : undefined;
}

/** "Number of items (p/st)", with "imports only" and the origins when it is limited. */
export function describeSupplementaryUnit(unit: {
  code: string;
  abbreviation: string | null;
  label: string | null;
  direction: 'both' | 'import';
  origins: string[] | null;
}): string {
  const label = unit.label ?? unit.code;
  const parts = [
    unit.abbreviation && unit.abbreviation !== label ? `${label} (${unit.abbreviation})` : label,
  ];
  if (unit.direction === 'import') parts.push('imports only');
  if (unit.origins?.length) parts.push(`goods from ${unit.origins.join(', ')}`);
  return parts.join(', ');
}
